import { useCallback } from 'react';

import { useMemberId } from '@trello/authentication';
import { useBoardId } from '@trello/id-context';
import { useSharedState, useSharedStateSelector } from '@trello/shared-state';

import { useBoardTypeFragment } from './BoardTypeFragment.generated';
import { cachedPersonalWorkspaceIdsState } from './cachedPersonalWorkspaceIdsState';
import type { PersonalWorkspaceIds } from './PersonalWorkspace.types';

export const useMemberInboxIds = (): Partial<PersonalWorkspaceIds> => {
  const memberId = useMemberId();
  const inboxIds = useSharedStateSelector(
    cachedPersonalWorkspaceIdsState,
    useCallback((state) => state[memberId], [memberId]),
  );

  return inboxIds || {};
};

export const useIsInboxBoardPassContextVals = ({
  boardId,
  memberId,
}: {
  boardId: string | null;
  memberId: string;
}) => {
  const [inboxIds] = useSharedState(cachedPersonalWorkspaceIdsState);
  const memberInboxIds = inboxIds[memberId];
  return Boolean(
    boardId && memberInboxIds?.idBoard && memberInboxIds.idBoard === boardId,
  );
};

export const useIsInboxBoardWithId = (boardId: string) => {
  const memberId = useMemberId();
  const { data } = useBoardTypeFragment({
    from: { id: boardId },
  });
  const isInboxBoard = data?.type === 'inbox';
  // fallback to board type check if inbox board is not cached
  // TODO: remove this once we have inbox available in the quickload
  return useIsInboxBoardPassContextVals({ boardId, memberId }) || isInboxBoard;
};

export const useIsInboxBoard = () => {
  const boardId = useBoardId();
  return useIsInboxBoardWithId(boardId);
};
